import { useMutation } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { defaultQuery, service, Test, useAuthConfig } from "@theprelude/core";
import {
  Button,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { testOverlayQueryOptions } from "@/queries/tests";
import { DefaultDialog } from "../default-dialog";

function useCopyTest() {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async ({ test }: { test: Test }) => {
      const { id } = await service(authConfig).build.cloneTest({
        source_test_id: test.id,
      });

      return id;
    },
    onError: (error) =>
      notifyError({
        message: "Unsuccessfully copied test",
        error,
        dismissible: true,
      }),
  });
}

export function CopyTestModal(props: {
  id: string;
  open: boolean;
  onOpenChange: (value: boolean) => any;
  close: () => void;
}) {
  return (
    <DefaultDialog open={props.open} onOpenChange={props.onOpenChange}>
      <CopyTestModalContent {...props} />
    </DefaultDialog>
  );
}

function CopyTestModalContent(props: { id: string; close: () => void }) {
  const authConfig = useAuthConfig();
  const navigate = useNavigate();
  const router = useRouterState();
  const testOverlayQuery = testOverlayQueryOptions(
    authConfig,
    defaultQuery,
    props.id,
  ).useSuspenseQuery();

  const { test } = testOverlayQuery.data;

  const copyTest = useCopyTest();

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Are you sure you want to copy the following test?{" "}
          {`${test.name} (*${test.id.slice(32, 36)})`}
        </DialogTitle>
      </DialogHeader>

      <DialogFooter>
        <DialogClose asChild>
          <Button intent="secondary">Cancel</Button>
        </DialogClose>
        <Button
          intent="primary"
          loading={copyTest.isPending}
          disabled={copyTest.isPending}
          onClick={() =>
            copyTest.mutate(
              {
                test,
              },
              {
                onSuccess: (id) => {
                  notifySuccess({
                    message: "Successfully copied test",
                  });
                  props.close();
                  void navigate({
                    to: router.location.pathname,
                    search: {
                      overlay: ["test", { id }],
                    },
                    params: (prev) => prev,
                  });
                },
              },
            )
          }
        >
          Copy test
        </Button>
      </DialogFooter>
    </>
  );
}
